import type Rollbar from 'rollbar';

export const REDACTED = '[REDACTED]';

const MAX_DEPTH = 20;
const SENSITIVE_KEY_SUFFIXES = ['password', 'passwd', 'passcode', 'secret', 'token', 'tokens', 'apikey', 'privatekey', 'authorization', 'cookie', 'credential', 'credentials', 'captcha'];
const SENSITIVE_EXACT_KEYS = ['pw', 'pass', 'otp', 'pin', 'cvc', 'cvv', 'sessionid'];

const JWT_PATTERN = /\beyJ[\w-]+\.[\w-]+\.[\w-]*/g;
const BEARER_PATTERN = /\bBearer\s+[\w.~+/=-]+/gi;
const SENSITIVE_PAIR_PATTERN =
  /((?:\\?")?[\w-]*(?:password|passwd|passcode|secret|tokens?|api[_-]?key|private[_-]?key|authorization|cookie|credentials?|captcha)(?:\\?")?\s*[:=]\s*)(\[REDACTED\]|\\"(?:(?!\\").)*\\"|"(?:[^"\\]|\\.)*"|[^\s"\\,&;}\]]+)/gi;

const normalizeKey = (key: string) => key.toLowerCase().replace(/[^a-z0-9]/g, '');

export function isSensitiveKey(key: string) {
  const normalized = normalizeKey(key);
  return SENSITIVE_EXACT_KEYS.includes(normalized) || SENSITIVE_KEY_SUFFIXES.some(suffix => normalized.endsWith(suffix));
}

export function redactSensitiveString(value: string) {
  return value
    .replace(JWT_PATTERN, REDACTED)
    .replace(BEARER_PATTERN, `Bearer ${REDACTED}`)
    .replace(SENSITIVE_PAIR_PATTERN, (_match, prefix: string, secret: string) => {
      if (secret.startsWith('\\"')) return `${prefix}\\"${REDACTED}\\"`;
      if (secret.startsWith('"')) return `${prefix}"${REDACTED}"`;
      return `${prefix}${REDACTED}`;
    });
}

function sanitizeValue(value: unknown, depth: number, ancestors: WeakSet<object>): unknown {
  if (typeof value === 'string') return redactSensitiveString(value);
  if (typeof value === 'function') return undefined;
  if (!value || typeof value !== 'object') return value;
  if (value instanceof Date) return value;
  if (ancestors.has(value)) return '[Circular]';
  if (depth >= MAX_DEPTH) return '[Truncated]';
  ancestors.add(value);

  if (Array.isArray(value)) {
    const items = value.map(item => sanitizeValue(item, depth + 1, ancestors));
    ancestors.delete(value);
    return items;
  }

  const source: Record<string, unknown> = value instanceof Error ? { ...value, name: value.name, message: value.message, stack: value.stack } : (value as Record<string, unknown>);
  const result: Record<string, unknown> = {};
  for (const key of Object.keys(source)) {
    const sanitized = isSensitiveKey(key) ? REDACTED : sanitizeValue(source[key], depth + 1, ancestors);
    if (sanitized !== undefined) result[key] = sanitized;
  }
  ancestors.delete(value);
  return result;
}

export function sanitizeTelemetry<T>(value: T): T {
  return sanitizeValue(value, 0, new WeakSet()) as T;
}

export const rollbarScrubOptions: Rollbar.Configuration = {
  scrubFields: [
    'token',
    'refreshToken',
    'firebaseToken',
    'idToken',
    'authorization',
    'cookie',
    'clientSecret',
    'client_secret',
    'apiKey',
    'api_key',
    'captcha',
    'otp',
    'newPassword',
    'oldPassword',
    'currentPassword'
  ],
  scrubTelemetryInputs: true,
  transform: payload => {
    Object.assign(payload, sanitizeTelemetry(payload));
  }
};
